import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useAppDispatch } from '../../../../app/hooks'
import {
  type CreateDisbursementAccountPayload,
  type DisbursementBankAccount,
  validateCreateDisbursementAccountPayload
} from '../../../../api/schema-dtos/AssetHolderDisbursementPayloadDtos'
import { Currency } from '../../../../api/asset-holders/Currency'
import Alert from '@mui/material/Alert'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import { Formik, type FormikProps } from 'formik'
import OtpComponent from '../../../auth/components/OtpComponent'
import { useCreateDisbursementAccountMutation } from '../../../../api/disbursements/disbursementApi'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'

interface AddDisbursementAccountComponentProps {
  assetHolderId: number
  hasPassedKyc: boolean
}

const AddDisbursementAccountComponent: React.FC<AddDisbursementAccountComponentProps> = ({ assetHolderId, hasPassedKyc }: AddDisbursementAccountComponentProps) => {
  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)

  const defaultZarForm: DisbursementBankAccount = {
    accountType: 'BankAccount',
    bankAccountNumber: '',
    accountHolderName: '',
    bankName: '',
    branchCode: ''
  }

  const defaultForm: CreateDisbursementAccountPayload = {
    assetHolderId,
    currencyCode: Currency.ZAR,
    accountDetails: defaultZarForm,
    otp: ''
  }

  const [apiCreateDisbursementAccount, { isLoading }] = useCreateDisbursementAccountMutation()

  const handleCreateDisbursementAccount = async (body: CreateDisbursementAccountPayload, otp: string): Promise<void> => {
    if (body.currencyCode === Currency.XBT) {
      delete (body.accountDetails as any).accountHolderName
      delete (body.accountDetails as any).bankName
      delete (body.accountDetails as any).bankAccountNumber
      delete (body.accountDetails as any).branchCode
    }

    if (body.currencyCode === Currency.ZAR) {
      delete (body.accountDetails as any).bitcoinAddress
    }

    apiCreateDisbursementAccount({
      assetHolderId,
      body: { ...body, otp }
    })
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Your cash out account details have been updated. Please check your email for your verification link. ' }))
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const changeCurrency = async (formikProps: FormikProps<CreateDisbursementAccountPayload>, newCurrencyCode: Currency): Promise<void> => {
    await formikProps.setFieldValue('currencyCode', newCurrencyCode)
    if (newCurrencyCode === Currency.XBT) {
      await formikProps.setFieldValue('accountDetails.accountType', 'BitcoinAddress')
      await formikProps.setFieldValue('accountDetails.bitcoinAddress', '')
    }
    if (newCurrencyCode === Currency.ZAR) {
      await formikProps.setFieldValue('accountDetails.accountType', 'BankAccount')
    }
    await formikProps.validateForm()
  }

  const listOfBanks: string[] = [
    'ABSA',
    'FNB',
    'Standard Bank',
    'Nedbank',
    'Capitec',
    'Capitec Business',
    'Investec Bank',
    'Rand Merchant Bank',
    'Tyme Bank',
    'Discovery Bank',
    'African Bank',
    'Wesbank',
    'Bidvest',
    'Ubank',
    'Bank Zero'
  ]

  const formatError = (formikProps: FormikProps<CreateDisbursementAccountPayload>): string => {
    const accountDetailsErrors = Object.values(formikProps.errors.accountDetails ?? []).join(', ')
    // Object.values(formikProps.errors).join(', ')
    return `You cannot create your cash out account until the following issues have been resolved: ${accountDetailsErrors}`
  }

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        disabled={!hasPassedKyc}
        onClick={() => { toggleDialog(true) }}
      >
        Add Cash Out Account
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'md'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => { toggleDialog(false) }}
        sx={{ p: 2 }}
      >
        <DialogTitle>Add Cash Out Account</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            validationSchema={validateCreateDisbursementAccountPayload}
            validateOnBlur={true}
            validateOnMount={true}
            onSubmit={(values) => {}}
          >
            {formikProps => (
              <form>
                <Grid container spacing={3} sx={{ pt: 2 }}>
                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={formatError(formikProps)}/>
                    </Grid>
                  )}

                  <Grid item md={12} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Payout Currency</InputLabel>
                      <Select
                        {...formikProps.getFieldProps('currencyCode')}
                        label="Payout Currency"
                        onChange={async event => { await changeCurrency(formikProps, (event.target.value as Currency)) }}
                      >
                        <MenuItem value={Currency.XBT}>BTC (Bitcoin)</MenuItem>
                        <MenuItem value={Currency.ZAR}>ZAR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <Typography variant={'subtitle1'}>Account Details</Typography>
                  </Grid>
                  {formikProps.values.currencyCode === Currency.XBT &&
                    <>
                      <Grid item md={12} xs={12}>
                        <TextField
                          label={'Bitcoin Address'}
                          type={'text'}
                          variant="outlined"
                          sx={{ width: '100%' }}
                          placeholder={'Bitcoin Address'}

                          {...formikProps.getFieldProps('accountDetails.bitcoinAddress')}
                        />
                      </Grid>
                    </>
                  }

                  {formikProps.values.currencyCode === Currency.ZAR &&
                    <>
                      <Grid item md={12} xs={12}>
                        <TextField
                          label={'Account Number'}
                          type={'text'}
                          variant="outlined"
                          sx={{ width: '100%' }}
                          placeholder={'Account Number'}
                          {...formikProps.getFieldProps('accountDetails.bankAccountNumber')}
                        />
                      </Grid>
                      <Grid item md={12} xs={12}>
                        <TextField
                          label={'Account Holder'}
                          type={'text'}
                          variant="outlined"
                          sx={{ width: '100%' }}
                          placeholder={'Account Holder'}

                          {...formikProps.getFieldProps('accountDetails.accountHolderName')}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Bank (South African Banks)</InputLabel>
                          <Select
                            {...formikProps.getFieldProps('accountDetails.bankName')}
                            label="Bank (South African Banks)"
                            onChange={async (event) => await formikProps.setFieldValue('accountDetails.bankName', event.target.value)}
                          >
                            {listOfBanks.map((bank: string, index: number) => (
                              <MenuItem key={index} value={bank}>{bank}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <TextField
                          label={'Branch Code'}
                          type={'text'}
                          variant="outlined"
                          sx={{ width: '100%' }}
                          placeholder={'Branch Code'}

                          {...formikProps.getFieldProps('accountDetails.branchCode')}
                        />
                      </Grid>
                    </>
                  }

                  <Grid item md={12} xs={12}>
                    <Alert severity="warning" >
                      Please note the following:
                      <p>
                        You will be unable to update cash out account details while you have pending cashout requests. Please cancel any requests first.
                      </p>
                      <p>
                        You will be asked to verify your new account details by clicking a verification link that will be sent to your email address.
                      </p>
                    </Alert>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <OtpComponent
                        assetHolderId={assetHolderId}
                        actionName={'Confirm'}
                        isLoading={isLoading}
                        authenticatorConfigured={null}
                        executeAction={async (otp: string) => { await handleCreateDisbursementAccount(formikProps.values, otp) } }
                        disabled={!formikProps.isValid || isLoading}
                      />

                      <Button
                        variant={'outlined'}
                        type="button"
                        sx={{ ml: 2 }}
                        onClick={() => { toggleDialog(false) }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AddDisbursementAccountComponent
